import React, { useState } from 'react';
import axios from 'axios';
import DuplicateManager from './DuplicateManager';
import ClassificationReview from './ClassificationReview';
import ExcelWorkflow from './ExcelWorkflow';
import { searchExercises } from '../../utils/exerciseSearch';
import ExerciseMergeScreen from './ExerciseMergeScreen';
import MuscleBackfillPanel from './MuscleBackfillPanel';
import MuscleMapPreviewModal from './MuscleMapPreviewModal';
import { PersonStanding } from 'lucide-react';
import { getExerciseAnimationUrl } from '../../utils/exerciseImages';
import { effectiveSetSeconds } from '../../utils/workoutDuration';
import { uploadExerciseImage } from '../../utils/uploadExerciseImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category border colors (hex) - matches the badge colors used throughout the app
const getCategoryBorderColor = (category) => {
  const colors = {
    'chest': '#ef4444',      // red-500
    'back': '#3b82f6',       // blue-500
    'legs': '#22c55e',       // green-500
    'shoulders': '#f97316',  // orange-500
    'arms': '#a855f7',       // purple-500
    'core': '#eab308',       // yellow-500
    'cardio': '#ec4899',     // pink-500
    'full-body': '#14b8a6',  // teal-500
    'stretching': '#06b6d4', // cyan-500
    'plyometrics': '#f59e0b' // amber-500
  };
  return colors[category] || '#6b7280'; // gray-500 fallback
};

const ExerciseManager = ({ exercises, onRefresh, onEdit, onAdd, language, t, onShowDetail }) => {
  const [showForm, setShowForm] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [deletingExerciseId, setDeletingExerciseId] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null); // { exercise, usageData }
  const [showDuplicateManager, setShowDuplicateManager] = useState(false);
  const [showExcelWorkflow, setShowExcelWorkflow] = useState(false);
  const [showMergeScreen, setShowMergeScreen] = useState(false); // Merge screen modal
  const [showClassificationReview, setShowClassificationReview] = useState(false);
  const [showMuscleBackfill, setShowMuscleBackfill] = useState(false);
  
  // Filtering state
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [muscleFilter, setMuscleFilter] = useState('all');
  const [equipmentFilter, setEquipmentFilter] = useState('all');
  const [movementPatternFilter, setMovementPatternFilter] = useState('all');
  const [exerciseTypeFilter, setExerciseTypeFilter] = useState('all');
  const [exerciseFamilyFilter, setExerciseFamilyFilter] = useState('all');
  const [showNoFamily, setShowNoFamily] = useState(false);
  const [showAiReviewed, setShowAiReviewed] = useState('all'); // 'all', 'reviewed', 'unreviewed'
  const [selectedExercises, setSelectedExercises] = useState(new Set());
  const [bulkActionMode, setBulkActionMode] = useState(false);
  const [showIncomplete, setShowIncomplete] = useState(false); // Filter for exercises missing educational content
  const [showMuscleIssues, setShowMuscleIssues] = useState(false);
  const [muscleAudit, setMuscleAudit] = useState(null); // { issues: {id: [codes]}, counts, total }

  React.useEffect(() => {
    if (!showMuscleIssues || muscleAudit) return;
    axios.get(`${API}/exercises/analysis/muscle-audit`)
      .then((r) => setMuscleAudit(r.data))
      .catch((e) => console.error('Error loading muscle audit:', e));
  }, [showMuscleIssues, muscleAudit]);
  
  // Usage sorting state
  const [usageSort, setUsageSort] = useState('none'); // 'none', 'most', 'least'
  const [usageStats, setUsageStats] = useState({}); // Map of exercise id -> usage_count
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [previewExercise, setPreviewExercise] = useState(null); // muscle map preview
  
  // Reference data (loaded once)
  const [equipmentList, setEquipmentList] = useState([]);
  
  // Load equipment reference data
  React.useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const response = await axios.get(`${API}/reference/equipment`);
        setEquipmentList(response.data.equipment || []);
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };
    loadReferenceData();
  }, []);
  
  // Load usage stats on component mount (always needed for display)
  React.useEffect(() => {
    const loadUsageStats = async () => {
      if (Object.keys(usageStats).length > 0) return; // Already loaded
      
      setLoadingUsage(true);
      try {
        const response = await axios.get(`${API}/exercises/usage-stats`);
        const statsMap = {};
        (response.data.exercises || []).forEach(ex => {
          statsMap[ex.id] = ex.usage_count;
          // Also map by name (lowercase) for fallback matching
          if (ex.name) {
            statsMap[ex.name.toLowerCase()] = ex.usage_count;
          }
        });
        setUsageStats(statsMap);
      } catch (error) {
        console.error('Error loading usage stats:', error);
      } finally {
        setLoadingUsage(false);
      }
    };
    loadUsageStats();
  }, [usageStats]);
  
  // Helper function to get equipment display name
  const getEquipmentName = (equipmentId) => {
    const eq = equipmentList.find(e => e.id === equipmentId);
    return eq ? (language === 'es' ? eq.name_es : eq.name) : equipmentId;
  };
  
  // Helper function to get translated name (uses database field or cache)
  const getTranslatedName = (exercise) => {
    if (language === 'en') {
      return exercise.name;
    }
    
    // First priority: Use name_es from database if available
    if (language === 'es' && exercise.name_es) {
      return exercise.name_es;
    }
    
    // Second priority: Check localStorage cache from modal translations
    try {
      const cacheKey = `ex_${exercise.id}_${language}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.content?.name || exercise.name;
      }
    } catch (e) {
      // Ignore cache errors
    }
    
    return exercise.name; // Fallback to original name
  };
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    equipment_ids: [],
    difficulty: '',
    instructions: '',
    instructions_es: '',
    image_url: '',
    image_path: '',
    primary_muscle_groups: [],
    movement_pattern: '',
    exercise_type: ''
  });
  const [uploadingImg, setUploadingImg] = useState(false);
  const [imgError, setImgError] = useState(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingImg(true);
    setImgError(null);
    try {
      const data = await uploadExerciseImage(file);
      setFormData(prev => ({ ...prev, image_url: data.image_url, image_path: data.image_path }));
    } catch (err) {
      setImgError(err.response?.data?.detail || t('image_upload_failed'));
    } finally {
      setUploadingImg(false);
    }
  };

  // PRIMARY MUSCLE GROUPS (fixed list, max 2 per exercise)
  const PRIMARY_MUSCLE_GROUPS = [
    'Chest', 'Triceps', 'Deltoids', 'Back', 'Lats', 
    'Biceps', 'Forearms', 'Quadriceps', 'Hamstrings', 'Glutes', 'Calves', 'Core'
  ];

  // MOVEMENT PATTERNS (v3 classification)
  const MOVEMENT_PATTERNS = [
    { id: 'squat', name: 'Squat' },
    { id: 'hinge', name: 'Hinge' },
    { id: 'horizontal_push', name: 'Horizontal Push' },
    { id: 'vertical_push', name: 'Vertical Push' },
    { id: 'horizontal_pull', name: 'Horizontal Pull' },
    { id: 'vertical_pull', name: 'Vertical Pull' },
    { id: 'carry', name: 'Carry' },
    { id: 'rotation', name: 'Rotation' },
    { id: 'anti_rotation', name: 'Anti-Rotation' },
    { id: 'isolation', name: 'Isolation' },
  ];

  // EXERCISE TYPES (v3 classification)
  const EXERCISE_TYPES = [
    { id: 'strength', name: 'Strength' },
    { id: 'plyometric', name: 'Plyometric' },
    { id: 'stretch', name: 'Stretch' },
    { id: 'mobility', name: 'Mobility' },
    { id: 'yoga', name: 'Yoga' },
    { id: 'cardio', name: 'Cardio' },
    { id: 'calisthenics', name: 'Calisthenics' },
    { id: 'rehab', name: 'Rehab' },
  ];

  // EXERCISE FAMILIES (Phase 1A — final 19, locked by user)
  const EXERCISE_FAMILIES = [
    { id: 'bench_press', name: 'Bench Press' },
    { id: 'overhead_press', name: 'Overhead Press' },
    { id: 'chest_fly', name: 'Chest Fly' },
    { id: 'dip', name: 'Dip' },
    { id: 'row', name: 'Row' },
    { id: 'pulldown_pullup', name: 'Pulldown / Pull-Up' },
    { id: 'rear_delt', name: 'Rear Delt' },
    { id: 'lateral_raise', name: 'Lateral Raise' },
    { id: 'front_raise', name: 'Front Raise' },
    { id: 'shrug', name: 'Shrug' },
    { id: 'curl', name: 'Curl' },
    { id: 'tricep_extension', name: 'Tricep Extension' },
    { id: 'squat', name: 'Squat' },
    { id: 'lunge', name: 'Lunge' },
    { id: 'hinge', name: 'Hinge' },
    { id: 'leg_curl', name: 'Leg Curl' },
    { id: 'leg_extension', name: 'Leg Extension' },
    { id: 'calf_raise', name: 'Calf Raise' },
    { id: 'core', name: 'Core' },
  ];
  const FAMILY_LABEL_BY_ID = EXERCISE_FAMILIES.reduce((acc, f) => {
    acc[f.id] = f.name;
    return acc;
  }, {});

  // Equipment Badge — Library QA Optimization
  // Maps raw equipment_ids → canonical operational label + soft tint color.
  // Non-canonical or missing equipment intentionally renders gray "Unknown"
  // to surface metadata cleanup opportunities while scrolling.
  const EQUIPMENT_BADGE_MAP = {
    barbell:       { label: 'Barbell',    cls: 'bg-red-500/20 text-red-200 border border-red-400/30' },
    dumbbell:      { label: 'Dumbbell',   cls: 'bg-orange-500/20 text-orange-200 border border-orange-400/30' },
    cable:         { label: 'Cable',      cls: 'bg-cyan-500/20 text-cyan-200 border border-cyan-400/30' },
    smith:         { label: 'Smith',      cls: 'bg-purple-500/20 text-purple-200 border border-purple-400/30' },
    bodyweight:    { label: 'Bodyweight', cls: 'bg-green-500/20 text-green-200 border border-green-400/30' },
    bands:         { label: 'Band',       cls: 'bg-yellow-500/20 text-yellow-200 border border-yellow-400/30' },
    'plate-machine':{ label: 'Machine',   cls: 'bg-blue-500/20 text-blue-200 border border-blue-400/30' },
    selectorized:  { label: 'Machine',    cls: 'bg-blue-500/20 text-blue-200 border border-blue-400/30' },
  };
  const EQUIPMENT_UNKNOWN_CLS = 'bg-gray-500/20 text-gray-300 border border-gray-400/30';

  // Pick the primary canonical equipment from an exercise.
  // Returns the first equipment_id that maps to a canonical entry; otherwise null.
  const resolveEquipmentBadge = (exercise) => {
    const ids = Array.isArray(exercise?.equipment_ids) ? exercise.equipment_ids : [];
    for (const id of ids) {
      if (EQUIPMENT_BADGE_MAP[id]) return EQUIPMENT_BADGE_MAP[id];
    }
    return { label: 'Unknown', cls: EQUIPMENT_UNKNOWN_CLS };
  };

  // Detect duplicates (exercises with very similar names)
  // Filter exercises based on search and filters
  const filteredExercises = React.useMemo(() => {
    let filtered = exercises;

    // Search by name (language-aware, accent-normalized, tokenized AND)
    if (searchTerm.trim()) {
      filtered = searchExercises(searchTerm, filtered, language);
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(ex => ex.category === categoryFilter);
    }

    // Filter by primary muscle group — STRICT: only uses primary_muscle_groups
    if (muscleFilter !== 'all') {
      filtered = filtered.filter(ex => 
        ex.primary_muscle_groups && ex.primary_muscle_groups.includes(muscleFilter)
      );
    }

    // Filter by equipment (NEW)
    if (equipmentFilter !== 'all') {
      filtered = filtered.filter(ex => 
        ex.equipment_ids && ex.equipment_ids.includes(equipmentFilter)
      );
    }

    // Filter by movement pattern (v3)
    if (movementPatternFilter !== 'all') {
      filtered = filtered.filter(ex => ex.movement_pattern === movementPatternFilter);
    }

    // Filter by exercise type (v3)
    if (exerciseTypeFilter !== 'all') {
      filtered = filtered.filter(ex => ex.exercise_type === exerciseTypeFilter);
    }

    // Filter by exercise family (Phase 1A)
    if (exerciseFamilyFilter !== 'all') {
      filtered = filtered.filter(ex => ex.exercise_family === exerciseFamilyFilter);
    }

    // Filter for exercises with NO family (admin review queue)
    if (showNoFamily) {
      filtered = filtered.filter(ex => !ex.exercise_family);
    }

    // Filter by AI enhancement status
    if (showAiReviewed === 'reviewed') {
      // Show only AI-enhanced exercises
      filtered = filtered.filter(ex => ex.ai_enhanced === true);
    } else if (showAiReviewed === 'unreviewed') {
      // Show only original (non-AI-enhanced) exercises
      filtered = filtered.filter(ex => !ex.ai_enhanced);
    }

    // Filter for incomplete exercises (missing educational content)
    if (showIncomplete) {
      filtered = filtered.filter(ex => {
        // Check if ANY of these fields are missing or empty (both ES and EN)
        const hasExecutionSteps = (ex.execution_steps && ex.execution_steps.length > 0) || 
                                   (ex.execution_steps_es && ex.execution_steps_es.length > 0) ||
                                   (ex.how_to_perform && ex.how_to_perform.trim()) ||
                                   (ex.how_to_perform_es && ex.how_to_perform_es.trim());
        
        const hasBenefits = (ex.benefits && ex.benefits.length > 0) || 
                            (ex.benefits_es && ex.benefits_es.length > 0);
        
        const hasCommonMistakes = (ex.common_mistakes && ex.common_mistakes.length > 0) || 
                                   (ex.common_mistakes_es && ex.common_mistakes_es.length > 0);
        
        const hasFormTips = (ex.form_tips && ex.form_tips.length > 0) || 
                            (ex.form_tips_es && ex.form_tips_es.length > 0);
        
        // Exercise is incomplete if ANY field is missing
        return !hasExecutionSteps || !hasBenefits || !hasCommonMistakes || !hasFormTips;
      });
    }

    // Muscle audit filter (missing primary groups / category mismatch)
    if (showMuscleIssues) {
      const issues = muscleAudit?.issues || {};
      filtered = filtered.filter(ex => issues[ex.id]);
    }

    // Sort by usage if enabled
    if (usageSort !== 'none' && Object.keys(usageStats).length > 0) {
      filtered = [...filtered].sort((a, b) => {
        const usageA = usageStats[a.id] || usageStats[a.name?.toLowerCase()] || 0;
        const usageB = usageStats[b.id] || usageStats[b.name?.toLowerCase()] || 0;
        return usageSort === 'most' ? usageB - usageA : usageA - usageB;
      });
    }

    return filtered;
  }, [exercises, searchTerm, categoryFilter, muscleFilter, equipmentFilter, movementPatternFilter, exerciseTypeFilter, exerciseFamilyFilter, showNoFamily, showAiReviewed, showIncomplete, showMuscleIssues, muscleAudit, usageSort, usageStats]);

  // Helper to get usage count for an exercise
  const getUsageCount = (exercise) => {
    return usageStats[exercise.id] || usageStats[exercise.name?.toLowerCase()] || 0;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setMuscleFilter('all');
    setEquipmentFilter('all');
    setMovementPatternFilter('all');
    setExerciseTypeFilter('all');
    setExerciseFamilyFilter('all');
    setShowNoFamily(false);
    setShowAiReviewed('all');
    setShowIncomplete(false);
    setShowMuscleIssues(false);
    setUsageSort('none');
  };

  const toggleExerciseSelection = (exerciseId) => {
    const newSelected = new Set(selectedExercises);
    if (newSelected.has(exerciseId)) {
      newSelected.delete(exerciseId);
    } else {
      newSelected.add(exerciseId);
    }
    setSelectedExercises(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedExercises.size === filteredExercises.length) {
      setSelectedExercises(new Set());
    } else {
      setSelectedExercises(new Set(filteredExercises.map(e => e.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedExercises.size === 0) {
      alert('Please select exercises to delete');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedExercises.size} exercises? This cannot be undone.`)) {
      return;
    }

    try {
      const exerciseIds = Array.from(selectedExercises);
      const response = await axios.post(`${API}/exercises/bulk-delete`, {
        exercise_ids: exerciseIds
      });
      
      alert(`✅ Successfully deleted ${response.data.deleted_count} exercises!`);
      setSelectedExercises(new Set());
      setBulkActionMode(false);
      onRefresh();
    } catch (error) {
      console.error('Error bulk deleting exercises:', error);
      alert('❌ Error deleting exercises. Check console for details.');
    }
  };

  const handleDelete = async (exercise) => {
    // ERP Phase 4.6: First check usage before showing confirmation
    setDeletingExerciseId(exercise.id);
    
    try {
      // Check exercise usage
      const usageResponse = await axios.get(`${API}/exercises/${exercise.id}/usage`);
      const usageData = usageResponse.data;
      
      // Show modal with usage information
      setDeleteConfirmModal({ exercise, usageData });
    } catch (error) {
      console.error('Error checking exercise usage:', error);
      // Fallback to simple confirmation
      if (window.confirm(`${t('confirm_delete_exercise')}\n\n"${exercise.name}"\n\n${t('this_action_cannot_be_undone')}`)) {
        await performDelete(exercise);
      }
    } finally {
      setDeletingExerciseId(null);
    }
  };

  // ERP Phase 4.6: Perform the actual deletion/archiving
  const performDelete = async (exercise) => {
    setDeletingExerciseId(exercise.id);
    try {
      const response = await axios.delete(`${API}/exercises/${exercise.id}`);
      onRefresh();
      
      if (response.data.action === 'archived') {
        alert(`✅ "${exercise.name}" has been archived.\n\nIt remains linked to ${response.data.usage_count} past workout(s) but is now hidden from the exercise library.`);
      } else {
        alert(`${t('exercise_deleted_successfully')}: ${exercise.name}`);
      }
    } catch (error) {
      console.error('Error deleting exercise:', error);
      alert(t('error_deleting_exercise') + ': ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeletingExerciseId(null);
      setDeleteConfirmModal(null);
    }
  };

  // ERP Phase 4.6: Handle confirmation from modal
  const handleConfirmDelete = () => {
    if (deleteConfirmModal?.exercise) {
      performDelete(deleteConfirmModal.exercise);
    }
  };

  const toggleAiReviewed = async (exercise) => {
    try {
      const response = await axios.patch(`${API}/exercises/${exercise.id}/toggle-review`);
      onRefresh();
      const newStatus = response.data.ai_reviewed;
      alert(newStatus ? '✅ Exercise marked as reviewed!' : '⚠️ Exercise marked as unreviewed');
    } catch (error) {
      console.error('Error toggling review status:', error);
      alert('❌ Error updating review status');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // muscle_groups is already an array, no need to process
      const submitData = {
        ...formData,
        image_path: formData.image_path || null,
        muscle_groups: formData.muscle_groups
      };
      
      await axios.post(`${API}/exercises`, submitData);
      setFormData({ 
        name: '', 
        category: '', 
        equipment: '',
        difficulty: '',
        instructions: '', 
        instructions_es: '',
        image_url: '',
        image_path: '',
        muscle_groups: []  // Reset to empty array
      });
      setShowForm(false);
      onRefresh();
    } catch (error) {
      console.error('Error creating exercise:', error);
      alert('Error creating exercise: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSeedExercises = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/exercises/seed`);
      alert(response.data.message);
      onRefresh();
    } catch (error) {
      console.error('Error seeding exercises:', error);
      alert('Error seeding exercises: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div>
      {/* Excel Workflow View */}
      {showExcelWorkflow ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">📊 Excel Import/Export</h2>
            <button
              onClick={() => setShowExcelWorkflow(false)}
              className="btn-primary bg-gray-600 hover:bg-gray-700"
            >
              ← Back to Library
            </button>
          </div>
          <ExcelWorkflow onRefresh={onRefresh} />
        </div>
      ) : showDuplicateManager ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">{t('exercise_library')}</h2>
            <button
              onClick={() => setShowDuplicateManager(false)}
              className="btn-primary bg-gray-600 hover:bg-gray-700"
            >
              ← Back to Library
            </button>
          </div>
          <DuplicateManager language={language} t={t} />
        </div>
      ) : showClassificationReview ? (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">Classification Review</h2>
            <button
              onClick={() => setShowClassificationReview(false)}
              className="btn-primary bg-gray-600 hover:bg-gray-700"
              data-testid="classification-back-btn"
            >
              ← Back to Library
            </button>
          </div>
          <ClassificationReview language={language} t={t} />
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">{t('exercise_library')}</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowExcelWorkflow(true)}
                className="btn-primary bg-emerald-600 hover:bg-emerald-700"
                title="Export/Import exercises via Excel"
              >
                📊 Excel Import/Export
              </button>
              <button
                onClick={() => setShowDuplicateManager(true)}
                className="btn-primary bg-purple-600 hover:bg-purple-700"
                title="Find and merge duplicate exercises"
              >
                🔍 Find Duplicates
              </button>
              <button
                onClick={() => setShowClassificationReview(true)}
                className="btn-primary bg-blue-600 hover:bg-blue-700"
                title="Review exercise classification (movement patterns, families)"
                data-testid="classification-review-btn"
              >
                Classification Review
              </button>
              <button
                onClick={() => setShowMuscleBackfill(true)}
                className="btn-primary bg-gradient-to-r from-fuchsia-600 to-indigo-600"
                title="Fill target/synergist/stabilizer muscles with AI"
                data-testid="muscle-backfill-btn"
              >
                💪 AI Muscle Data
              </button>
              {exercises.length < 50 && (
                <button
                  onClick={handleSeedExercises}
                  disabled={seeding}
                  className="btn-primary bg-green-600 hover:bg-green-700"
                >
                  {seeding ? 'Seeding...' : t('load_complete_database')}
                </button>
              )}
              <button
                onClick={() => (onAdd ? onAdd() : setShowForm(!showForm))}
                className="btn-primary"
                data-testid="add-exercise-btn"
              >
                {t('add_exercise')}
              </button>
            </div>
          </div>

      {/* Filters Section */}
      <div className="glass-card p-4 mb-6">
        {/* Main Filter Row - Search & Filters Only */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
          {/* Search by name */}
          <div className="col-span-2 md:col-span-1">
            <label className="block text-blue-200 text-xs font-medium mb-1">🔍 {t('search_by_name')}</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('search_exercises')}
              className="input-field text-sm"
            />
          </div>

          {/* Category filter */}
          <div>
            <label className="block text-blue-200 text-xs font-medium mb-1">📂 {t('category')}</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="input-field text-sm"
            >
              <option value="all">{t('all_categories')}</option>
              <option value="chest">{t('chest')}</option>
              <option value="back">{t('back')}</option>
              <option value="legs">{t('legs')}</option>
              <option value="shoulders">{t('shoulders')}</option>
              <option value="arms">{t('arms')}</option>
              <option value="core">{t('core')}</option>
              <option value="cardio">{t('cardio')}</option>
              <option value="full-body">{t('full-body')}</option>
              <option value="stretching">{t('stretching')}</option>
              <option value="plyometrics">{t('plyometrics')}</option>
              <option value="neck">{t('neck')}</option>
            </select>
          </div>

          {/* Primary Muscle Group filter */}
          <div>
            <label className="block text-blue-200 text-xs font-medium mb-1">💪 {t('primary_muscle')}</label>
            <select
              value={muscleFilter}
              onChange={(e) => setMuscleFilter(e.target.value)}
              className="input-field text-sm"
            >
              <option value="all">{t('all_muscles')}</option>
              {PRIMARY_MUSCLE_GROUPS.map(muscle => (
                <option key={muscle} value={muscle}>{muscle}</option>
              ))}
            </select>
          </div>

          {/* Equipment filter */}
          <div>
            <label className="block text-blue-200 text-xs font-medium mb-1">🔧 {t('equipment')}</label>
            <select
              value={equipmentFilter}
              onChange={(e) => setEquipmentFilter(e.target.value)}
              className="input-field text-sm"
            >
              <option value="all">{t('all_equipment')}</option>
              {equipmentList.map(eq => (
                <option key={eq.id} value={eq.id}>
                  {language === 'es' ? eq.name_es : eq.name}
                </option>
              ))}
            </select>
          </div>

          {/* Movement Pattern filter (v3) */}
          <div>
            <label className="block text-blue-200 text-xs font-medium mb-1" data-testid="movement-pattern-filter-label">Movement</label>
            <select
              value={movementPatternFilter}
              onChange={(e) => setMovementPatternFilter(e.target.value)}
              className="input-field text-sm"
              data-testid="movement-pattern-filter"
            >
              <option value="all">All Patterns</option>
              {MOVEMENT_PATTERNS.map(mp => (
                <option key={mp.id} value={mp.id}>{mp.name}</option>
              ))}
            </select>
          </div>

          {/* Exercise Type filter (v3) */}
          <div>
            <label className="block text-blue-200 text-xs font-medium mb-1" data-testid="exercise-type-filter-label">Type</label>
            <select
              value={exerciseTypeFilter}
              onChange={(e) => setExerciseTypeFilter(e.target.value)}
              className="input-field text-sm"
              data-testid="exercise-type-filter"
            >
              <option value="all">All Types</option>
              {EXERCISE_TYPES.map(et => (
                <option key={et.id} value={et.id}>{et.name}</option>
              ))}
            </select>
          </div>

          {/* Exercise Family filter (Phase 1A) */}
          <div>
            <label className="block text-blue-200 text-xs font-medium mb-1" data-testid="exercise-family-filter-label">🧬 Family</label>
            <select
              value={exerciseFamilyFilter}
              onChange={(e) => setExerciseFamilyFilter(e.target.value)}
              className="input-field text-sm"
              data-testid="exercise-family-filter"
            >
              <option value="all">All Families</option>
              {EXERCISE_FAMILIES.map(fam => (
                <option key={fam.id} value={fam.id}>{fam.name}</option>
              ))}
            </select>
          </div>

          {/* Usage Sort */}
          <div>
            <label className="block text-blue-200 text-xs font-medium mb-1">📊 Usage</label>
            <select
              value={usageSort}
              onChange={(e) => setUsageSort(e.target.value)}
              className="input-field text-sm"
              data-testid="usage-sort-select"
            >
              <option value="none">Unsorted</option>
              <option value="most">Most used</option>
              <option value="least">Least used</option>
            </select>
          </div>
        </div>

        {/* Secondary Actions Area */}
        <div className="flex flex-wrap gap-2 items-center mt-4 pt-3 border-t border-blue-400/20">
          <button
            onClick={clearFilters}
            className="px-3 py-1.5 bg-gray-600/50 hover:bg-gray-600 text-gray-200 rounded text-xs font-medium transition"
          >
            🔄 {t('clear_filters')}
          </button>

          <span className="text-blue-400/40 mx-1">|</span>

          <button
            onClick={() => setShowIncomplete(!showIncomplete)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition ${
              showIncomplete
                ? 'bg-yellow-500 text-white'
                : 'bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30'
            }`}
            title="Show exercises without complete educational content"
          >
            {showIncomplete ? '✓ ' : ''}⚠️ Incomplete exercises
          </button>

          <button
            onClick={() => setShowMuscleIssues(!showMuscleIssues)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition ${
              showMuscleIssues
                ? 'bg-red-500 text-white'
                : 'bg-red-500/20 text-red-300 hover:bg-red-500/30'
            }`}
            title="Exercises with missing primary muscle groups or groups that don't match their category"
            data-testid="muscle-issues-toggle"
          >
            {showMuscleIssues ? '✓ ' : ''}💪 Muscle issues{muscleAudit ? ` (${muscleAudit.total})` : ''}
          </button>

          {/* Phase 1A: Show only exercises missing exercise_family (admin review queue) */}
          <button
            onClick={() => setShowNoFamily(!showNoFamily)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition ${
              showNoFamily
                ? 'bg-pink-500 text-white'
                : 'bg-pink-500/20 text-pink-300 hover:bg-pink-500/30'
            }`}
            title="Show only exercises without an exercise_family (review queue)"
            data-testid="no-family-toggle"
          >
            {showNoFamily ? '✓ ' : ''}🧬 No Family
          </button>

          <button
            onClick={() => {
              if (showAiReviewed === 'all') setShowAiReviewed('reviewed');
              else if (showAiReviewed === 'reviewed') setShowAiReviewed('unreviewed');
              else setShowAiReviewed('all');
            }}
            className={`px-3 py-1.5 rounded text-xs font-medium transition ${
              showAiReviewed === 'reviewed'
                ? 'bg-purple-500 text-white'
                : showAiReviewed === 'unreviewed'
                ? 'bg-gray-500 text-white'
                : 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
            }`}
            title="Filter AI-enhanced exercises"
          >
            {showAiReviewed === 'reviewed' ? '🤖 AI-Enhanced' : showAiReviewed === 'unreviewed' ? '📝 Original' : '📚 All'}
          </button>
          
          <button
            onClick={() => {
              setBulkActionMode(!bulkActionMode);
              setSelectedExercises(new Set());
            }}
            className={`px-3 py-1.5 rounded text-xs font-medium transition ${
              bulkActionMode
                ? 'bg-orange-500 text-white'
                : 'bg-orange-500/20 text-orange-300 hover:bg-orange-500/30'
            }`}
          >
            {bulkActionMode ? '✓ ' : ''}☑️ {t('bulk_select_mode')}
          </button>

          {/* Results count - inline */}
          <span className="text-blue-400/40 mx-1">|</span>
          <span className="text-blue-200 text-xs">
            <strong className="text-green-300">{filteredExercises.length}</strong> / {exercises.length} {t('exercises_shown')}
          </span>
        </div>

        {/* Bulk Actions Bar */}
        {bulkActionMode && (
          <div className="mt-3 bg-orange-500/20 border border-orange-400/40 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleSelectAll}
                className="btn-secondary text-sm"
              >
                {selectedExercises.size === filteredExercises.length ? '☑️' : '☐'} {t('select_all')} ({filteredExercises.length})
              </button>
              <span className="text-orange-200 font-medium text-sm">
                {selectedExercises.size} {t('selected')}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* MERGE BUTTON - Only enabled when exactly 2 exercises selected */}
              <button
                onClick={() => setShowMergeScreen(true)}
                disabled={selectedExercises.size !== 2}
                className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                  selectedExercises.size === 2
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-gray-500/50 text-gray-400 cursor-not-allowed'
                }`}
                title={selectedExercises.size === 2 
                  ? 'Merge these 2 exercises' 
                  : 'Select exactly 2 exercises to merge'}
                data-testid="merge-selected-btn"
              >
                🔗 {language === 'es' ? 'Fusionar' : 'Merge'} {selectedExercises.size === 2 ? '(2)' : ''}
              </button>
              
              {selectedExercises.size > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-sm font-medium"
                >
                  🗑️ {t('delete_selected')} ({selectedExercises.size})
                </button>
              )}
              <button
                onClick={() => {
                  setBulkActionMode(false);
                  setSelectedExercises(new Set());
                }}
                className="btn-secondary text-sm"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <div className="glass-card p-6 mb-6">
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2">{t('exercise_name')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2">{t('category')}</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="input-field"
                required
              >
                <option value="">{t('select_category')}</option>
                <option value="chest">{t('chest')}</option>
                <option value="back">{t('back')}</option>
                <option value="legs">{t('legs')}</option>
                <option value="shoulders">{t('shoulders')}</option>
                <option value="arms">{t('arms')}</option>
                <option value="core">{t('core')}</option>
                <option value="cardio">{t('cardio')}</option>
                <option value="full-body">{t('full-body')}</option>
                <option value="stretching">{t('stretching')}</option>
                <option value="plyometrics">{t('plyometrics')}</option>
                <option value="neck">{t('neck')}</option>
              </select>
            </div>
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2">⭐ {t('difficulty')}</label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                className="input-field"
                required
              >
                <option value="">{t('select_difficulty')}</option>
                <option value="beginner">{t('beginner')}</option>
                <option value="intermediate">{t('intermediate')}</option>
                <option value="advanced">{t('advanced')}</option>
              </select>
            </div>
            {/* Movement Pattern (v3) */}
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2">Movement Pattern</label>
              <select
                value={formData.movement_pattern}
                onChange={(e) => setFormData({...formData, movement_pattern: e.target.value})}
                className="input-field"
                data-testid="form-movement-pattern"
              >
                <option value="">Select pattern...</option>
                {MOVEMENT_PATTERNS.map(mp => (
                  <option key={mp.id} value={mp.id}>{mp.name}</option>
                ))}
              </select>
            </div>
            {/* Exercise Type (v3) */}
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2">Exercise Type</label>
              <select
                value={formData.exercise_type}
                onChange={(e) => setFormData({...formData, exercise_type: e.target.value})}
                className="input-field"
                data-testid="form-exercise-type"
              >
                <option value="">Select type...</option>
                {EXERCISE_TYPES.map(et => (
                  <option key={et.id} value={et.id}>{et.name}</option>
                ))}
              </select>
            </div>
            {/* Primary Muscle Groups (max 2) */}
            <div className="col-span-2">
              <label className="block text-blue-200 text-sm font-medium mb-2">💪 {t('primary_muscles')} (max 2)</label>
              <div className="flex flex-wrap gap-2">
                {PRIMARY_MUSCLE_GROUPS.map(muscle => (
                  <button
                    key={muscle}
                    type="button"
                    onClick={() => {
                      const current = formData.primary_muscle_groups || [];
                      if (current.includes(muscle)) {
                        setFormData({...formData, primary_muscle_groups: current.filter(m => m !== muscle)});
                      } else if (current.length < 2) {
                        setFormData({...formData, primary_muscle_groups: [...current, muscle]});
                      }
                    }}
                    className={`px-3 py-1 rounded text-sm transition ${
                      formData.primary_muscle_groups?.includes(muscle)
                        ? 'bg-purple-500 text-white'
                        : 'bg-purple-500/20 text-purple-200 hover:bg-purple-500/40'
                    }`}
                  >
                    {muscle}
                  </button>
                ))}
              </div>
            </div>
            {/* Equipment Selection */}
            <div className="col-span-2">
              <label className="block text-blue-200 text-sm font-medium mb-2">🔧 {t('equipment')}</label>
              <div className="flex flex-wrap gap-2">
                {equipmentList.map(eq => (
                  <button
                    key={eq.id}
                    type="button"
                    onClick={() => {
                      const current = formData.equipment_ids || [];
                      if (current.includes(eq.id)) {
                        setFormData({...formData, equipment_ids: current.filter(e => e !== eq.id)});
                      } else {
                        setFormData({...formData, equipment_ids: [...current, eq.id]});
                      }
                    }}
                    className={`px-3 py-1 rounded text-sm transition ${
                      formData.equipment_ids?.includes(eq.id)
                        ? 'bg-cyan-500 text-white'
                        : 'bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/40'
                    }`}
                  >
                    {language === 'es' ? eq.name_es : eq.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-blue-200 text-sm font-medium mb-2">🖼️ {t('image_url')}</label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value, image_path: ''})}
                className="input-field"
                placeholder="https://example.com/exercise.gif"
                data-testid="add-exercise-image-url-input"
              />
              <div className="flex items-center gap-3 mt-2">
                <span className="text-blue-300 text-xs">{t('or_label')}</span>
                <label
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium cursor-pointer transition ${uploadingImg ? 'bg-slate-600 text-slate-300 cursor-wait' : 'bg-cyan-500/90 hover:bg-cyan-400 text-white'}`}
                  data-testid="add-exercise-image-upload-label"
                >
                  <span>📁</span>
                  {uploadingImg ? t('uploading_image') : t('upload_image')}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingImg}
                    onChange={handleImageUpload}
                    data-testid="add-exercise-image-upload-input"
                  />
                </label>
              </div>
              {imgError && (
                <p className="text-red-400 text-xs mt-1" data-testid="add-exercise-image-upload-error">{imgError}</p>
              )}
              {formData.image_url && (
                <div className="mt-2">
                  <img
                    src={formData.image_url}
                    alt="Exercise preview"
                    className="max-w-xs h-32 object-contain rounded-lg bg-black/20"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    data-testid="add-exercise-image-preview"
                  />
                </div>
              )}
              <p className="text-blue-300 text-xs mt-1">
                {t('image_url_help')}
              </p>
            </div>
            <div className="col-span-2">
              <label className="block text-blue-200 text-sm font-medium mb-2">{t('instructions')}</label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                className="input-field"
                rows="3"
                placeholder="Brief exercise instructions..."
              />
            </div>
            <div className="col-span-2">
              <label className="block text-blue-200 text-sm font-medium mb-2">{t('instructions_spanish')}</label>
              <textarea
                value={formData.instructions_es}
                onChange={(e) => setFormData({...formData, instructions_es: e.target.value})}
                className="input-field"
                rows="3"
                placeholder="Instrucciones en español..."
              />
            </div>
            <div className="col-span-2">
              <button type="submit" className="btn-primary">
                {t('add_exercise')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* REDESIGNED: Visual-first Exercise Library Grid */}
      {/* 4-5 columns on desktop, large GIF previews, clean layout */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {filteredExercises.map((exercise) => (
          <div 
            key={exercise.id} 
            className={`glass-card relative overflow-hidden group transition-all duration-200 hover:ring-1 hover:ring-blue-400/50 ${
              selectedExercises.has(exercise.id) ? 'ring-2 ring-orange-500' : ''
            }`}
            style={{ borderLeft: `4px solid ${getCategoryBorderColor(exercise.category)}` }}
            data-testid={`exercise-card-${exercise.id}`}
          >
            {/* Bulk Selection Checkbox */}
            {bulkActionMode && (
              <div className="absolute top-2 left-2 z-20">
                <input
                  type="checkbox"
                  checked={selectedExercises.has(exercise.id)}
                  onChange={() => toggleExerciseSelection(exercise.id)}
                  className="w-5 h-5 cursor-pointer rounded"
                />
              </div>
            )}
            
            {/* GIF Preview - Dominant Element with subtle hover scale */}
            <div 
              className="relative w-full aspect-square bg-gradient-to-br from-blue-900/50 to-purple-900/50 cursor-pointer overflow-hidden"
              onClick={() => onShowDetail(exercise)}
            >
              {(exercise.animation_webp || exercise.image_url) ? (
                <img 
                  src={getExerciseAnimationUrl(exercise)} 
                  alt={exercise.name}
                  className="w-full h-full object-cover transition-transform duration-[250ms] ease-out group-hover:scale-[1.03]"
                  loading="lazy"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-5xl opacity-30">🏋️</span>
                </div>
              )}
              
              {/* Hover Overlay with Actions - Only visible on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end justify-center pb-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowDetail(exercise);
                    }}
                    className="bg-green-500/90 hover:bg-green-500 text-white p-2 rounded-lg transition-all transform scale-90 group-hover:scale-100"
                    title={t('view_details')}
                    data-testid={`view-exercise-${exercise.id}`}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewExercise(exercise);
                    }}
                    className="bg-purple-500/90 hover:bg-purple-500 text-white p-2 rounded-lg transition-all transform scale-90 group-hover:scale-100"
                    title={language === 'es' ? 'Ver mapa muscular' : 'View muscle map'}
                    data-testid={`muscle-preview-exercise-${exercise.id}`}
                  >
                    <PersonStanding className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(exercise);
                    }}
                    className="bg-blue-500/90 hover:bg-blue-500 text-white p-2 rounded-lg transition-all transform scale-90 group-hover:scale-100"
                    title={t('edit')}
                    data-testid={`edit-exercise-${exercise.id}`}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(exercise);
                    }}
                    disabled={deletingExerciseId === exercise.id}
                    className="bg-red-500/90 hover:bg-red-500 text-white p-2 rounded-lg transition-all transform scale-90 group-hover:scale-100 disabled:opacity-50"
                    title={t('delete')}
                    data-testid={`delete-exercise-${exercise.id}`}
                  >
                    {deletingExerciseId === exercise.id ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Usage Count - Subtle overlay in corner */}
              <div 
                className="absolute top-2 right-2 text-xs px-1.5 py-0.5 rounded bg-black/50 text-white/70"
                title={`Usado en ${getUsageCount(exercise)} entrenamientos`}
              >
                {getUsageCount(exercise)}
              </div>

              {showMuscleIssues && muscleAudit?.issues?.[exercise.id] && (
                <div
                  className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-1"
                  data-testid={`muscle-issue-badge-${exercise.id}`}
                >
                  {muscleAudit.issues[exercise.id].map((code) => (
                    <span key={code} className="text-[10px] px-1.5 py-0.5 rounded bg-red-600/90 text-white font-medium">
                      {code === 'missing_primary' ? 'No primary muscles' : `Category ≠ ${(exercise.primary_muscle_groups || []).join(', ')}`}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* Card Content - Minimal text */}
            <div className="p-3">
              {/* Exercise Name - Bold */}
              <h3 
                className="font-bold text-white text-sm leading-tight mb-2 line-clamp-2 cursor-pointer hover:text-blue-200"
                onClick={() => onShowDetail(exercise)}
                title={getTranslatedName(exercise)}
              >
                {getTranslatedName(exercise)}
              </h3>
              
              {/* Badges Row - Category + Primary Muscles + Equipment (Library QA) */}
              <div className="flex flex-wrap gap-1">
                {/* Category Badge - Same style as Workouts/Bookings */}
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  exercise.category === 'chest' ? 'bg-red-500/25 text-red-300' :
                  exercise.category === 'back' ? 'bg-blue-500/25 text-blue-300' :
                  exercise.category === 'legs' ? 'bg-green-500/25 text-green-300' :
                  exercise.category === 'shoulders' ? 'bg-orange-500/25 text-orange-300' :
                  exercise.category === 'arms' ? 'bg-purple-500/25 text-purple-300' :
                  exercise.category === 'core' ? 'bg-yellow-500/25 text-yellow-300' :
                  exercise.category === 'cardio' ? 'bg-pink-500/25 text-pink-300' :
                  exercise.category === 'full-body' ? 'bg-teal-500/25 text-teal-300' :
                  exercise.category === 'stretching' ? 'bg-cyan-500/25 text-cyan-300' :
                  exercise.category === 'plyometrics' ? 'bg-amber-500/25 text-amber-300' :
                  'bg-gray-500/25 text-gray-300'
                }`}>
                  {t(exercise.category) || exercise.category}
                </span>
                
                {/* Primary Muscles - Badge style */}
                {(exercise.primary_muscle_groups || exercise.muscle_groups || []).slice(0, 2).map((muscle, idx) => (
                  <span 
                    key={idx} 
                    className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300"
                  >
                    {muscle}
                  </span>
                ))}

                {/* Equipment badge — replaces Family badge for fast metadata QA */}
                {(() => {
                  const eq = resolveEquipmentBadge(exercise);
                  const isUnknown = eq.label === 'Unknown';
                  return (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${eq.cls}`}
                      title={isUnknown
                        ? 'No canonical equipment assigned (metadata review)'
                        : `Equipment: ${eq.label}`}
                      data-testid={`equipment-badge-${exercise.id}`}
                    >
                      {eq.label}
                    </span>
                  );
                })()}

                {/* Duration chip — effective work-seconds/set (hybrid) */}
                {(() => {
                  const eff = effectiveSetSeconds(exercise);
                  const cls = {
                    manual: 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/30',
                    learned: 'bg-blue-500/20 text-blue-200 border border-blue-400/30',
                    default: 'bg-gray-500/20 text-gray-300 border border-gray-400/30',
                  }[eff.source];
                  const marker = { manual: '✎', learned: '⟳', default: '·' }[eff.source];
                  const srcLabel = { manual: 'manual override', learned: 'auto-learned', default: 'category default' }[eff.source];
                  return (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${cls}`}
                      title={`~${Math.round(eff.seconds)}s per set (${srcLabel})`}
                      data-testid={`duration-chip-${exercise.id}`}
                    >
                      ⏱ {Math.round(eff.seconds)}s {marker}
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>
        ))}
      </div>
        </>
      )}

      {/* ERP Phase 4.6: Delete Confirmation Modal */}
      {deleteConfirmModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="delete-confirm-modal">
          <div className="glass-card p-6 max-w-md mx-4 max-h-[90dvh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">
              {deleteConfirmModal.usageData?.will_archive ? '⚠️ Archive Exercise?' : '🗑️ Delete Exercise?'}
            </h3>
            
            <div className="mb-4">
              <p className="text-blue-200 font-medium mb-2">"{deleteConfirmModal.exercise?.name}"</p>
              
              {deleteConfirmModal.usageData?.will_archive ? (
                <div className="bg-yellow-500/20 border border-yellow-400/40 rounded-lg p-4 mb-4">
                  <p className="text-yellow-200 font-medium mb-2">
                    ⚠️ This exercise has historical usage!
                  </p>
                  <p className="text-yellow-100 text-sm mb-2">
                    It appears in <strong>{deleteConfirmModal.usageData.usage_count}</strong> workout(s).
                  </p>
                  <p className="text-yellow-100/80 text-sm">
                    To preserve your client's workout history and metrics, this exercise will be <strong>archived</strong> instead of deleted:
                  </p>
                  <ul className="text-yellow-100/80 text-sm mt-2 list-disc list-inside space-y-1">
                    <li>Hidden from exercise library</li>
                    <li>Won't appear in exercise selectors</li>
                    <li>Past workouts remain intact</li>
                    <li>Historical metrics preserved</li>
                  </ul>
                </div>
              ) : (
                <div className="bg-red-500/20 border border-red-400/40 rounded-lg p-4 mb-4">
                  <p className="text-red-200 text-sm">
                    This exercise has no historical usage. It will be <strong>permanently deleted</strong>.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmModal(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deletingExerciseId}
                className={`px-4 py-2 rounded-lg transition font-medium ${
                  deleteConfirmModal.usageData?.will_archive
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-black'
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
                data-testid="confirm-delete-btn"
              >
                {deletingExerciseId ? '⏳...' : (deleteConfirmModal.usageData?.will_archive ? '📦 Archive' : '🗑️ Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exercise Merge Screen Modal */}
      {showMergeScreen && selectedExercises.size === 2 && (
        <ExerciseMergeScreen
          selectedExercises={Array.from(selectedExercises)}
          exercises={exercises}
          onClose={() => setShowMergeScreen(false)}
          onMergeComplete={(result) => {
            setShowMergeScreen(false);
            setSelectedExercises(new Set());
            setBulkActionMode(false);
            onRefresh();
            
            // Show success message
            if (result.action === 'hard_delete') {
              alert(`✅ "${result.exercise_id}" has been permanently deleted.`);
            } else {
              alert(`✅ Successfully merged!\n\n"${result.duplicate?.name}" → "${result.canonical?.name}"\n\n${result.stats?.workouts_updated || 0} workouts updated.`);
            }
          }}
          language={language}
          t={t}
        />
      )}
      {showMuscleBackfill && (
        <MuscleBackfillPanel onClose={() => { setShowMuscleBackfill(false); onRefresh(); }} />
      )}
      {previewExercise && (
        <MuscleMapPreviewModal
          exercise={previewExercise}
          language={language}
          onClose={() => setPreviewExercise(null)}
        />
      )}
    </div>
  );
};

// Exercise Detail Modal Component (rendered at root level)

export default ExerciseManager;
